import React, { useState } from 'react';

import { useApp } from '../context/AppContext';
import './Developer.css';

const SCOPES = ['contacts:read','contacts:write','deals:read','deals:write','evaluations:read','evaluations:write','payments:read','webhooks:manage','scouts:read','clubs:read'];

const WEBHOOK_EVENTS = ['club_account_created','club_profile_completed','player_request_submitted','scout_app_download','scout_profile_created','scout_club_connected','evaluation_assigned','evaluation_submitted','evaluation_purchased','scout_payment_processed'];

const CODE_EXAMPLES = {
  js: `const response = await fetch('https://api.transferexpo.com/v1/contacts', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer te_live_sk_prod_a8f3...',
    'Content-Type': 'application/json'
  }
});
const data = await response.json();
console.log(data.contacts);`,

  python: `import requests

response = requests.get(
  'https://api.transferexpo.com/v1/contacts',
  headers={
    'Authorization': 'Bearer te_live_sk_prod_a8f3...',
    'Content-Type': 'application/json'
  }
)
print(response.json())`,

  curl: `curl -X GET https://api.transferexpo.com/v1/contacts \\
  -H "Authorization: Bearer te_live_sk_prod_a8f3..." \\
  -H "Content-Type: application/json"`,
};

const ENDPOINTS = [
  { method:'GET',    path:'/v1/contacts',                desc:'List all contacts with optional filters' },
  { method:'POST',   path:'/v1/contacts',                desc:'Create a new contact' },
  { method:'GET',    path:'/v1/contacts/:id',            desc:'Get a single contact by ID' },
  { method:'PATCH',  path:'/v1/contacts/:id',            desc:'Update a contact\'s properties' },
  { method:'DELETE', path:'/v1/contacts/:id',            desc:'Delete a contact permanently' },
  { method:'GET',    path:'/v1/clubs',                   desc:'List all clubs with status filters' },
  { method:'POST',   path:'/v1/clubs/:id/activate',      desc:'Manually activate a club' },
  { method:'GET',    path:'/v1/scouts',                  desc:'List scouts with earnings data' },
  { method:'GET',    path:'/v1/deals',                   desc:'List all deals across pipelines' },
  { method:'POST',   path:'/v1/webhooks',                desc:'Register a new webhook endpoint' },
  { method:'GET',    path:'/v1/webhooks/:id/deliveries', desc:'View webhook delivery history' },
  { method:'POST',   path:'/v1/evaluations',             desc:'Trigger a player evaluation request' },
  { method:'GET',    path:'/v1/payments',                desc:'List all payment transactions' },
];

const methodColor = m => ({GET:'#A7F432', POST:'#4fc3f7', PATCH:'#f5a623', DELETE:'#ff4d4d'})[m] || '#8a9a97';

export default function Developer() {
  const { toast } = useApp();
  const [keys, setKeys]       = useState(apiKeys);
  const [hooks, setHooks]     = useState(webhooks);
  const [tab, setTab]         = useState('keys');
  const [lang, setLang]       = useState('js');
  const [showNewKey, setShowNewKey]   = useState(false);
  const [showNewHook, setShowNewHook] = useState(false);
  const [newKey, setNewKey]   = useState({ name:'', scopes:['contacts:read'] });
  const [newHook, setNewHook] = useState({ name:'', url:'', event:'' });
  const [showKey, setShowKey] = useState(null);

  const copyKey = k => { navigator.clipboard?.writeText(k); toast('API key copied to clipboard'); };

  const createKey = () => {
    if (!newKey.name) return;
    const k = { id: Date.now(), name: newKey.name, key:`te_live_sk_${Math.random().toString(36).slice(2,8)}...`, created: new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}), lastUsed:'Never', scope: newKey.scopes, status:'Active' };
    setKeys(prev => [...prev, k]);
    setShowKey(k.key.replace('...','_FULL_KEY_SHOWN_ONCE'));
    setShowNewKey(false);
    setNewKey({ name:'', scopes:['contacts:read'] });
    toast('API key created — copy it now, it won\'t be shown again', 'warning');
  };

  const revokeKey = id => {
    setKeys(prev => prev.map(k => k.id===id ? {...k, status:'Revoked'} : k));
    toast('API key revoked');
  };

  const createHook = () => {
    if (!newHook.name || !newHook.url || !newHook.event) return;
    const h = { id: Date.now(), ...newHook, status:'Active', deliveries:0, lastFired:'Never' };
    setHooks(prev => [...prev, h]);
    setShowNewHook(false);
    setNewHook({ name:'', url:'', event:'' });
    toast('Webhook registered successfully');
  };

  const toggleHook = id => {
    setHooks(prev => prev.map(h => h.id===id ? {...h, status: h.status==='Active'?'Inactive':'Active'} : h));
    toast('Webhook status updated');
  };

  const toggleScope = scope => setNewKey(prev => ({
    ...prev,
    scopes: prev.scopes.includes(scope) ? prev.scopes.filter(s=>s!==scope) : [...prev.scopes, scope]
  }));

  return (
    <div className="dev-page">
      {/* Header */}
      <div className="dev-hero">
        <div>
          <h2 className="dev-hero-title">Developer Portal</h2>
          <p className="dev-hero-sub">API keys, webhooks, documentation and integration tools for TransferExpo</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <a className="btn btn-ghost" href="https://docs.transferexpo.com" target="_blank" rel="noreferrer">View full docs ↗</a>
          <button className="btn btn-primary" onClick={() => setShowNewKey(true)}>+ Create API key</button>
        </div>
      </div>

      {/* Base URL callout */}
      <div className="dev-callout">
        <span className="dev-callout-label">Base URL</span>
        <code>https://api.transferexpo.com/v1</code>
        <button className="dev-copy-btn" onClick={() => { navigator.clipboard?.writeText('https://api.transferexpo.com/v1'); toast('Copied'); }}>Copy</button>
      </div>

      {/* Tabs */}
      <div className="dev-tabs">
        {[['keys','API Keys'],['webhooks','Webhooks'],['endpoints','Endpoints'],['quickstart','Quickstart']].map(([id,label]) => (
          <button key={id} className={`dev-tab ${tab===id?'active':''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── API KEYS ── */}
      {tab === 'keys' && (
        <div>
          {showKey && (
            <div className="key-reveal">
              <div className="key-reveal-title">⚠ Copy your API key — it won't be shown again</div>
              <div className="key-reveal-row">
                <code className="key-code">{showKey}</code>
                <button className="btn btn-primary" style={{fontSize:12}} onClick={() => { navigator.clipboard?.writeText(showKey); toast('Copied'); }}>Copy key</button>
              </div>
              <button className="key-reveal-dismiss" onClick={() => setShowKey(null)}>I've saved my key — dismiss</button>
            </div>
          )}

          {showNewKey && (
            <div className="card" style={{marginBottom:16}}>
              <div className="card-header"><span className="card-title">New API key</span></div>
              <div style={{display:'flex',flexDirection:'column',gap:14}}>
                <div><label className="field-label">Key name</label><input className="search-input" placeholder="e.g. Production — Club Portal" value={newKey.name} onChange={e=>setNewKey(p=>({...p,name:e.target.value}))} /></div>
                <div>
                  <label className="field-label">Scopes (permissions)</label>
                  <div className="scope-grid">
                    {SCOPES.map(s => (
                      <button key={s} className={`scope-pill ${newKey.scopes.includes(s)?'active':''}`} onClick={() => toggleScope(s)}>{s}</button>
                    ))}
                  </div>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <button className="btn btn-primary" onClick={createKey}>Generate key</button>
                  <button className="btn btn-ghost" onClick={() => setShowNewKey(false)}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div className="card" style={{padding:0}}>
            <div className="card-header" style={{padding:'14px 20px',borderBottom:'1px solid var(--border)'}}>
              <span className="card-title">API keys</span>
              <span style={{fontSize:12,color:'var(--text-dim)'}}>{keys.filter(k=>k.status==='Active').length} active</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Key</th><th>Scopes</th><th>Last used</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {keys.map(k => (
                    <tr key={k.id}>
                      <td style={{fontWeight:500}}>{k.name}</td>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <code style={{fontSize:12,color:'var(--accent)',background:'rgba(167,244,50,0.08)',padding:'2px 6px',borderRadius:4}}>{k.key}</code>
                          {k.status==='Active' && <button className="dev-copy-btn" onClick={()=>copyKey(k.key)}>Copy</button>}
                        </div>
                      </td>
                      <td><div style={{display:'flex',gap:4,flexWrap:'wrap'}}>{k.scope?.slice(0,2).map(s=><span key={s} className="scope-tag">{s}</span>)}{k.scope?.length>2&&<span className="scope-tag">+{k.scope.length-2}</span>}</div></td>
                      <td style={{fontSize:12,color:'var(--text-dim)'}}>{k.lastUsed}</td>
                      <td><span className={`badge ${k.status==='Active'?'badge-green':'badge-red'}`}>{k.status}</span></td>
                      <td>{k.status==='Active'&&<button className="btn btn-danger" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>revokeKey(k.id)}>Revoke</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── WEBHOOKS ── */}
      {tab === 'webhooks' && (
        <div>
          {showNewHook && (
            <div className="card" style={{marginBottom:16}}>
              <div className="card-header"><span className="card-title">Register webhook</span></div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
                <div><label className="field-label">Webhook name</label><input className="search-input" placeholder="e.g. Club Created Handler" value={newHook.name} onChange={e=>setNewHook(p=>({...p,name:e.target.value}))} /></div>
                <div><label className="field-label">Endpoint URL (HTTPS)</label><input className="search-input" placeholder="https://your-server.com/webhook" value={newHook.url} onChange={e=>setNewHook(p=>({...p,url:e.target.value}))} /></div>
              </div>
              <div style={{marginBottom:14}}>
                <label className="field-label">Event to listen for</label>
                <select className="search-input" value={newHook.event} onChange={e=>setNewHook(p=>({...p,event:e.target.value}))}>
                  <option value="">Select event...</option>
                  {WEBHOOK_EVENTS.map(e=><option key={e}>{e}</option>)}
                </select>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-primary" onClick={createHook}>Register webhook</button>
                <button className="btn btn-ghost" onClick={()=>setShowNewHook(false)}>Cancel</button>
              </div>
            </div>
          )}

          {!showNewHook && <button className="btn btn-primary" style={{marginBottom:16}} onClick={()=>setShowNewHook(true)}>+ Register webhook</button>}

          <div className="card" style={{padding:0}}>
            <div className="card-header" style={{padding:'14px 20px',borderBottom:'1px solid var(--border)'}}>
              <span className="card-title">Webhooks</span>
              <span style={{fontSize:12,color:'var(--text-dim)'}}>{hooks.filter(h=>h.status==='Active').length} active</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Endpoint</th><th>Event</th><th>Deliveries</th><th>Last fired</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {hooks.map(h => (
                    <tr key={h.id}>
                      <td style={{fontWeight:500}}>{h.name}</td>
                      <td><code style={{fontSize:11,color:'var(--text-muted)'}}>{h.url}</code></td>
                      <td><code style={{fontSize:11,color:'var(--accent)',background:'rgba(167,244,50,0.08)',padding:'2px 6px',borderRadius:4}}>{h.event}</code></td>
                      <td style={{fontWeight:600}}>{h.deliveries}</td>
                      <td style={{fontSize:12,color:'var(--text-dim)'}}>{h.lastFired}</td>
                      <td><span className={`badge ${h.status==='Active'?'badge-green':'badge-gray'}`}>{h.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={()=>toggleHook(h.id)}>{h.status==='Active'?'Disable':'Enable'}</button>
                          <button className="btn btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={()=>toast('Test ping sent')}>Ping</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Webhook payload example */}
          <div className="card" style={{marginTop:16}}>
            <div className="card-header"><span className="card-title">Example webhook payload</span></div>
            <pre className="code-block">{`POST https://your-server.com/webhook
Content-Type: application/json
X-TransferExpo-Signature: sha256=abc123...

{
  "event": "club_account_created",
  "timestamp": "2026-04-03T09:00:00Z",
  "data": {
    "contact_id": "c_001",
    "company": "Arsenal FC",
    "transferexpo_contact_type": "Club",
    "onboarding_status": "In Progress",
    "country": "United Kingdom"
  }
}`}</pre>
          </div>
        </div>
      )}

      {/* ── ENDPOINTS ── */}
      {tab === 'endpoints' && (
        <div>
          <div className="card" style={{padding:0}}>
            <div className="card-header" style={{padding:'14px 20px',borderBottom:'1px solid var(--border)'}}><span className="card-title">API endpoints</span></div>
            <div className="endpoint-list">
              {ENDPOINTS.map((ep,i) => (
                <div key={i} className="endpoint-row">
                  <span className="method-badge" style={{background:`${methodColor(ep.method)}22`,color:methodColor(ep.method)}}>{ep.method}</span>
                  <code className="endpoint-path">{ep.path}</code>
                  <span className="endpoint-desc">{ep.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── QUICKSTART ── */}
      {tab === 'quickstart' && (
        <div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Quickstart guide</span>
              <div style={{display:'flex',gap:4}}>
                {['js','python','curl'].map(l=>(
                  <button key={l} className={`lang-btn ${lang===l?'active':''}`} onClick={()=>setLang(l)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="qs-steps">
              <div className="qs-step"><div className="qs-num">1</div><div><div className="qs-title">Get your API key</div><p className="qs-desc">Create an API key from the API Keys tab above. Store it securely — never expose it in client-side code.</p></div></div>
              <div className="qs-step"><div className="qs-num">2</div><div><div className="qs-title">Make your first request</div><pre className="code-block">{CODE_EXAMPLES[lang]}</pre></div></div>
              <div className="qs-step"><div className="qs-num">3</div><div><div className="qs-title">Set up webhooks</div><p className="qs-desc">Register a webhook endpoint to receive real-time events when contacts are created, clubs activate, or evaluations are submitted.</p></div></div>
              <div className="qs-step"><div className="qs-num">4</div><div><div className="qs-title">Handle events</div><p className="qs-desc">Verify the <code>X-TransferExpo-Signature</code> header using HMAC-SHA256 with your webhook secret before processing any event.</p></div></div>
            </div>
          </div>

          <div className="card" style={{marginTop:16}}>
            <div className="card-header"><span className="card-title">Rate limits</span></div>
            <div className="rate-table">
              {[['Basic','500 contacts max','100 req/min','No API access'],['Pro','5,000 contacts max','500 req/min','Read-only API'],['Enterprise','Unlimited','2,000 req/min','Full read + write API']].map(([tier,...cells])=>(
                <div key={tier} className="rate-row">
                  <span style={{fontWeight:600,color:'var(--text-primary)',minWidth:100}}>{tier}</span>
                  {cells.map((c,i)=><span key={i} style={{color:'var(--text-muted)',fontSize:13}}>{c}</span>)}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
